import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema';
import { IconButton } from '@atlaskit/button/new';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import Tooltip from '@atlaskit/tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { PageRemixModal } from '@confluence/malleable-ui-common/entry-points/PageRemixContainer.ts';
import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useSmartCreationModalState } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { isEmptyAdf } from '@confluence/comments-util/entry-points/domUtils.ts';
import { useRightSidebarContext } from '@confluence/page-layout-context';

const remixButtonContainerStyle = css({
	position: 'relative',
});

const i18n = defineMessages({
	remixPageLabel: {
		id: 'object-sidebar-components.remix-page.label',
		defaultMessage: 'Remix page',
		description: 'Label for the button that opens the page-level remix overlay',
	},
	remixPageTooltip: {
		id: 'object-sidebar-components.remix-page.tooltip',
		defaultMessage: 'Remix',
		description: 'Tooltip for the button that opens the object-level remix overlay',
	},
	remixPageEmptyTooltip: {
		id: 'object-sidebar-components.remix-page.empty-tooltip',
		defaultMessage: 'Add content to this page to remix',
		description: 'Tooltip shown when the remix button is disabled because the page has no content',
	},
	remixInProgressTooltip: {
		id: 'object-sidebar-components.remix-page.in-progress-tooltip',
		defaultMessage: 'Remix in progress',
		description:
			'Tooltip shown when the remix button is disabled because a remix is already in progress',
	},
});

const parseAdf = (value: string): DocNode | null => {
	try {
		const parsed = JSON.parse(value);
		if (!parsed || parsed.type !== 'doc') {
			return null;
		}
		return parsed as DocNode;
	} catch {
		return null;
	}
};

export const RemixPanelButton = ({
	contentBodyValue,
	getAdfDocument,
	isEditorEmpty,
	isDisabled = false,
}: {
	contentBodyValue?: string | null;
	getAdfDocument?: () => Promise<DocNode | null>;
	isEditorEmpty?: boolean;
	isDisabled?: boolean;
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const { showSmartCreationModal } = useSmartCreationModalState();
	const rightSidebarContext = useRightSidebarContext();
	const { width: rightSidebarWidth } = rightSidebarContext.getCurrent() || {};

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isThinking, setIsThinking] = useState(false);
	const [lazyAdfDocument, setLazyAdfDocument] = useState<DocNode | null>(null);
	const [viewOpenAdfDocument, setViewOpenAdfDocument] = useState<DocNode | null>(null);

	const hasContentFromBody = useMemo(() => {
		if (getAdfDocument) {
			return false;
		}
		if (!contentBodyValue) {
			return false;
		}
		const parsed = parseAdf(contentBodyValue);
		return parsed !== null && !isEmptyAdf(parsed);
	}, [contentBodyValue, getAdfDocument]);

	const hasContent = getAdfDocument ? !isEditorEmpty : hasContentFromBody;

	const resolvedAdfDocument = getAdfDocument ? lazyAdfDocument : viewOpenAdfDocument;

	const closeModal = useCallback(() => {
		setIsModalOpen(false);
		setIsThinking(false);
		setViewOpenAdfDocument(null);
		setLazyAdfDocument(null);
	}, []);

	const handleClick = useCallback(async () => {
		if (!hasContent || isThinking || showSmartCreationModal) {
			return;
		}
		if (isModalOpen) {
			closeModal();
			return;
		}
		if (getAdfDocument) {
			const doc = await getAdfDocument();
			if (doc) {
				setLazyAdfDocument(doc);
				setIsModalOpen(true);
			}
		} else if (contentBodyValue) {
			const doc = parseAdf(contentBodyValue);
			if (doc) {
				setViewOpenAdfDocument(doc);
				setIsModalOpen(true);
			}
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remix',
				source: 'remixModalFromObjectSidebar',
				attributes: {
					entryPoint: 'objectSidebar',
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		hasContent,
		isModalOpen,
		isThinking,
		showSmartCreationModal,
		closeModal,
		getAdfDocument,
		contentBodyValue,
	]);

	const onStartThinking = useCallback(() => {
		setIsThinking(true);
		setIsModalOpen(false);
	}, []);

	const onStopThinking = useCallback((options?: { reopenModal?: boolean }) => {
		setIsThinking(false);
		if (options?.reopenModal) {
			setIsModalOpen(true);
		}
	}, []);

	// Stop thinking when the ephemeral preview (smart creation modal) is closed
	// while thinking is still active, to prevent the thinking bar from reappearing.
	// This matches the pattern used in EditorRemixCreator's Wrapper component.
	const isSmartCreationModalOpen = useRef(false);
	useLayoutEffect(() => {
		if (isSmartCreationModalOpen.current && !showSmartCreationModal && isThinking) {
			onStopThinking();
		}
		isSmartCreationModalOpen.current = showSmartCreationModal;
	}, [showSmartCreationModal, isThinking, onStopThinking]);

	const tooltipContent =
		isModalOpen && !isThinking && !showSmartCreationModal
			? undefined
			: isThinking || showSmartCreationModal
				? formatMessage(i18n.remixInProgressTooltip)
				: hasContent
					? formatMessage(i18n.remixPageTooltip)
					: formatMessage(i18n.remixPageEmptyTooltip);

	return (
		<div css={remixButtonContainerStyle}>
			<ConfluenceConversationAssistant>
				<PageRemixModal
					adfDocument={resolvedAdfDocument ?? ({ type: 'doc', version: 1, content: [] } as DocNode)}
					onClose={closeModal}
					onStartThinking={onStartThinking}
					onStopThinking={onStopThinking}
					isEphemeralViewVisible={showSmartCreationModal}
					popupPlacement="left-end"
					isPopupOpen={isModalOpen || isThinking}
					popupRepositionKey={rightSidebarWidth}
					trigger={(triggerProps) => (
						<Tooltip position="left" content={tooltipContent} hideTooltipOnClick>
							<IconButton
								{...triggerProps}
								onClick={handleClick}
								appearance="subtle"
								shape={isRovoButtonEnabled ? 'default' : 'circle'}
								icon={AiGenerativeRemixIcon}
								label={formatMessage(i18n.remixPageLabel)}
								testId="sidebar-remix-button"
								isDisabled={isDisabled || !hasContent || isThinking || showSmartCreationModal}
								isSelected={isModalOpen && !isThinking && !showSmartCreationModal}
							/>
						</Tooltip>
					)}
				/>
			</ConfluenceConversationAssistant>
		</div>
	);
};
